<template>
  <div class="modal-overlay" @click.self="closeModal">
    <div class="modal-container">
      <!-- Modal Header -->
      <div class="modal-header">
        <div class="title-section">
          <i class="fa fa-exclamation-triangle warning-icon"></i>
          <h3 class="modal-title">Gate Pass In Required</h3>
        </div>
        <button class="close-btn" @click="closeModal" aria-label="Close modal">
          <i class="fa fa-times"></i>
        </button>
      </div>

      <!-- Modal Body -->
      <div class="modal-body">
        <!-- Error / Warning Message Box -->
        <div class="alert-box">
          <i class="fa fa-info-circle alert-icon"></i>
          <p class="alert-message">
            {{ message || 'Before creating a new dispatch, ensure that the gate pass is in for dispatches from more than 2 days ago.' }}
          </p>
        </div>

        <div class="table-section">
          <h4 class="section-title">Pending Dispatches</h4>
          <div class="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>Vehicle No</th>
                  <th>Dispatch No</th>
                  <th>Date</th>
                  <th class="actions-header">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(dispatch, index) in dispatches" :key="index" class="table-row">
                  <td class="font-semibold text-dark">
                    <i class="fa fa-truck vehicle-icon mr-2"></i>
                    {{ dispatch.vehicle_no }}
                  </td>
                  <td>
                    <span class="dispatch-badge">{{ dispatch.dispatch_no }}</span>
                  </td>
                  <td>
                    <span class="date-badge">{{ formatDate(dispatch.date) }}</span>
                  </td>
                  <td class="actions-cell">
                    <button class="btn-action" @click="openGatePassIn(dispatch)" title="Open Gate Pass In in a new tab">
                      <i class="fa fa-external-link"></i>
                      <span>Gate Pass In</span>
                    </button>
                  </td>
                </tr>
                <tr v-if="!dispatches || dispatches.length === 0">
                  <td colspan="4" class="text-center text-muted py-3">No pending dispatches found.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "GatePassConflictModal",
  props: {
    message: {
      type: String,
      default: ""
    },
    dispatches: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  mounted() {
    document.body.style.overflow = "hidden";
  },
  beforeUnmount() {
    document.body.style.overflow = "auto";
  },
  beforeDestroy() {
    document.body.style.overflow = "auto";
  },
  methods: {
    closeModal() {
      this.$emit("close");
    },
    formatDate(dateString) {
      if (!dateString) return "N/A";
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      } catch (e) {
        return dateString;
      }
    },
    openGatePassIn(dispatch) {
      const routeData = this.$router.resolve({
        path: "/gate-pass-in",
        query: {
          return_type: dispatch.return_type || "Dispatch",
          vehicle_id: dispatch.vehicle_id,
          date: dispatch.date
        }
      });
      window.open(routeData.href, "_blank");
    }
  }
};
</script>

<style scoped>
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 3000;
  animation: fadeIn 0.25s ease-out;
}

.modal-container {
  background-color: #ffffff;
  width: 90%;
  max-width: 900px;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  max-height: 85vh;
  animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  overflow: hidden;
  box-sizing: border-box;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  background-color: #fff;
  border-bottom: 1px solid #f1f5f9;
}

.title-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.warning-icon {
  font-size: 1.4rem;
  color: #d97706;
}

.modal-title {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e293b;
}

.close-btn {
  background: transparent;
  border: none;
  font-size: 1.2rem;
  color: #64748b;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background-color: #f1f5f9;
  color: #0f172a;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.alert-box {
  background-color: #fffbe6;
  border-left: 4px solid #d97706;
  padding: 16px;
  border-radius: 6px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 24px;
}

.alert-icon {
  font-size: 1.25rem;
  color: #d97706;
  margin-top: 2px;
  flex-shrink: 0;
}

.alert-message {
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.5;
  color: #78350f;
  font-weight: 500;
}

.section-title {
  margin: 0 0 14px 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #475569;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.table-section {
  display: flex;
  flex-direction: column;
}

.table-responsive {
  width: 100%;
  overflow-x: auto;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  font-size: 0.9rem;
}

th {
  background-color: #f8fafc;
  color: #475569;
  font-weight: 600;
  padding: 14px 16px;
  border-bottom: 2px solid #e2e8f0;
  white-space: nowrap;
}

td {
  padding: 14px 16px;
  border-bottom: 1px solid #e2e8f0;
  color: #334155;
  vertical-align: middle;
}

.table-row {
  transition: background-color 0.15s ease;
}

.table-row:hover {
  background-color: #f8fafc;
}

.font-semibold {
  font-weight: 600;
}

.text-dark {
  color: #0f172a;
}

.vehicle-icon {
  color: #2563eb;
}

.dispatch-badge {
  display: inline-block;
  background-color: #f1f5f9;
  color: #1e293b;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 0.85rem;
}

.date-badge {
  display: inline-block;
  background-color: #f8fafc;
  color: #475569;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.85rem;
  white-space: nowrap;
}

.actions-header, .actions-cell {
  text-align: center;
}

.btn-action {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background-color: #2563eb;
  color: #ffffff;
  border: none;
  padding: 7px 14px;
  border-radius: 6px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.btn-action:hover {
  background-color: #1d4ed8;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(37, 99, 235, 0.2);
}

.btn-action:active {
  transform: translateY(0);
}

.mr-1 { margin-right: 4px; }
.mr-2 { margin-right: 8px; }
</style>
